import type { ReactNode } from 'react';
import styles from './SessionList.module.css';
import { Icon } from './Icon.js';
import { ICONS } from '../icons/index.js';

/**
 * **`SessionList`**（§5.5 / D12 / D21）。
 *
 * ## 中断と再開はチャット履歴の再表示のみ（D12）
 *
 * §4.7:「**専用のサマリを生成しない**（D12）。
 * **1 問 = 1 セッションなので履歴は『解いた問題の一覧』として読める**」。
 *
 * **要約の prop を持たせない** — 持たせると、いつか誰かが生成する
 * （**1 ターン分の課金が増え、D12 の判断が黙って覆る**）。
 *
 * ## `hiddenByUser` は削除ではない（D20 / D21）
 *
 * **学習者の削除は自分の一覧から消すだけ**で、**研修記録は削除しない**。
 * **`includeHidden` は講師ビューの経路**であり、学習者の画面には出ない。
 *
 * ## 並べ替えを持たない
 *
 * **API が新しい順で返す**（GSI1 のソートキー）。
 * **UI が並べ替えると 2 つ目の順序ができる**（`UnderstandingPanel` と同じ思想）。
 */
export interface SessionListItem {
  readonly sessionId: string;
  /** **問題の見出し**（要約ではない。D12） */
  readonly problemLabel: string;
  readonly createdAt: string;
  readonly hiddenByUser?: boolean;
}

export interface SessionListProps {
  /** **API が新しい順で返したもの**（UI は並べ替えない） */
  readonly sessions: readonly SessionListItem[];
  readonly selectedId?: string;
  readonly onSelect: (sessionId: string) => void;
}

export function SessionList({ sessions, selectedId, onSelect }: SessionListProps): ReactNode {
  return (
    <nav aria-label="これまでの問題">
      {/** **見出しを置く**（モック）。**日付でグループ化しない** — 「今日 / 先週」の
        * 判定はクライアントの時計に依存し、**同じ一覧が端末ごとに違って見える** */}
      {sessions.length === 0 ? null : (
        <p className={styles['groupLabel']}>
          <Icon svg={ICONS.history} size={16} />
          これまでの問題
        </p>
      )}
      <ul className={styles['list']}>
        {sessions.map((s) => {
          const selected = s.sessionId === selectedId;
          return (
            <li key={s.sessionId}>
              <button
                type="button"
                className={[
                  styles['row'],
                  selected ? styles['selected'] : undefined,
                  s.hiddenByUser === true ? styles['hidden'] : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                /** **選択を読み上げにも伝える**（見た目の差は届かない） */
                aria-current={selected ? 'page' : undefined}
                onClick={() => onSelect(s.sessionId)}
              >
                {/** **1 件であることを形で示す**（M16。文字だけの列は走査しにくい） */}
                <Icon svg={ICONS.documents} size={16} />
                <span className={styles['rowLabel']}>{s.problemLabel}</span>
                <span className={styles['date']}>{s.createdAt.slice(5, 10)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
